import { CSSProperties, useEffect, useRef, useState } from 'react';
import { DropzoneOptions, FileRejection, useDropzone } from 'react-dropzone';

import { Stack, Typography } from '@mui/material';
import clsx from 'clsx';

import { IcUpload } from '@/assets/images';
import { FilePickerDefaultOptions } from '@/constants';
import { IFile } from '@/models';
import { colors } from '@/theme/variables';
import { getErrorMessage } from '@/utils';

import { FileList } from './FileList';
import { FilePickerRoot } from './styles';

export type FilePickerDirection = 'vertical' | 'horizontal';

export interface FilePickerProps
  extends Omit<DropzoneOptions, 'onDropAccepted' | 'onDropRejected'> {
  className?: string;
  style?: CSSProperties;
  initialFileItems?: IFile[];
  onChange: (files: IFile[]) => void;
  allowedFileTypes?: string[];
  itemClassName?: string;
  enableEditDescription?: boolean;
  direction?: FilePickerDirection;
  preventUpload?: boolean;
  disablePreview?: boolean;
  isInFileGrid?: boolean;
  label?: string;
  readOnly?: boolean;
}

export const FilePicker = (props: FilePickerProps) => {
  const {
    className,
    style,
    initialFileItems = [],
    onChange,
    accept,
    itemClassName,
    enableEditDescription,
    direction = 'horizontal',
    preventUpload,
    disablePreview = false,
    isInFileGrid = false,
    disabled = false,
    readOnly = false,
    ...rest
  } = props;

  const [fileItems, setFileItems] = useState<IFile[]>([]);
  const prevInitialItemsRef = useRef<IFile[]>();

  useEffect(() => {
    if (
      initialFileItems &&
      initialFileItems.length > 0 &&
      initialFileItems !== prevInitialItemsRef.current
    ) {
      prevInitialItemsRef.current = initialFileItems;
      setFileItems(initialFileItems);
    }
  }, [initialFileItems]);

  const onDropAccepted = (newAcceptedFiles: File[]) => {
    const newFileItems = newAcceptedFiles.map((file) => ({
      name: file.name,
      size: file.size,
      mimeType: file.type,
      file,
      description: '',
    }));
    setFileItems((v) => {
      onChange([...newFileItems, ...v]);
      return [...newFileItems, ...v];
    });
  };

  const onDropRejected = (rejectedFiles: FileRejection[]) => {
    const rejectedFileItems = rejectedFiles.map(({ file, errors }) => {
      return {
        file: {
          ...file,
          path: file.path ?? '',
        },
        name: file.name,
        url: '',
        size: file.size,
        mimeType: file.type,
        error: getErrorMessage(errors[0]?.code, errors[0]?.message),
      };
    });
    setFileItems((v) => {
      onChange([...v, ...rejectedFileItems]);
      return [...v, ...rejectedFileItems];
    });
  };

  const nameLengthValidator = (file: File) => {
    if (file.name.length > 100) {
      return {
        code: 'name-too-large',
        message: 'Name is larger than 100 characters',
      };
    }
    return null;
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      ...(accept || { 'image/jpeg': [], 'image/png': [] }),
    },
    ...FilePickerDefaultOptions,
    ...rest,
    disabled,
    onDropAccepted,
    onDropRejected,
    validator: nameLengthValidator,
  });

  const handleRemove = (index: number) => {
    const remainedFileItems = fileItems
      .slice(0, index)
      .concat(fileItems.slice(index + 1));
    setFileItems(remainedFileItems);
    onChange(remainedFileItems);
  };

  return (
    <FilePickerRoot
      justifyContent="center"
      alignItems="center"
      className={clsx(className, {
        'vertical-aligned-file-picker': direction === 'vertical',
      })}
      style={style}
      isInFileGrid={isInFileGrid}
    >
      {!disabled && (
        <Stack
          {...getRootProps({})}
          className={`pickerBox${
            preventUpload ? ' pickerBox--prohibited' : ''
          }`}
        >
          <input {...getInputProps()} />

          <IcUpload color="red" />

          <Typography className="title1" textAlign="center" padding={1}>
            Drop a file here to upload, or
          </Typography>
          <Typography
            className="title2"
            textAlign="center"
            color={colors.error.main}
          >
            click here to browse
          </Typography>
        </Stack>
      )}
      {!disablePreview && (
        <FileList
          files={fileItems}
          direction={direction}
          itemClassName={itemClassName}
          enableEditDescription={enableEditDescription}
          onRemove={handleRemove}
          disabled={disabled}
          readOnly={readOnly}
        />
      )}
    </FilePickerRoot>
  );
};
